/* @flow */
import idx from 'idx';
import React from 'react';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import PhoneCallPreferenceSection from 'settings/components/PhoneCallPreferenceSection.jsx';

type Props = {
  smsSupported: boolean,
  mutate: (variables: Object) => Promise<*>,
  data: ReactApolloData<PhoneCallPreferencePermissionQuery>,
};

type State = {
  persisted: boolean,
  persistError: ?Error,
};

function buildOptimisticMutationResponse(props: Props, value: string) {
  const uuid = idx(props.data, _ => _.me.uuid);

  if (!uuid) {
    return undefined;
  }

  return {
    __typename: 'Mutation',
    updateUserPrefs: {
      __typename: 'UpdateUserPrefsPayload',
      user: {
        __typename: 'User',
        uuid,
        preferences: {
          __typename: 'UserPreferences',
          receive_phone_calls: value,
        },
      },
    },
  };
}

class PhoneCallPreferenceSectionContainer extends React.Component<Props, State> {
  state: State = {
    persisted: true,
    persistError: null,
  };

  listen = (mutator: () => Promise<*>): void => {
    this.setState({ persisted: false, persistError: null });

    mutator()
      .then(() => this.setState({ persisted: true }))
      .catch(e => this.setState({ persistError: e }));
  };

  enable = (): void => {
    this.listen(() =>
      this.props.mutate({
        variables: { value: 'enabled' },
        optimisticResponse: buildOptimisticMutationResponse(this.props, 'enabled'),
      })
    );
  };

  disable = (): void => {
    this.listen(() =>
      this.props.mutate({
        variables: { value: 'disabled' },
        optimisticResponse: buildOptimisticMutationResponse(this.props, 'disabled'),
      })
    );
  };

  isInitialDataMissing(): boolean {
    return !idx(this.props.data, _ => _.me);
  }

  hasInitialDataErrored(): boolean {
    return !!idx(this.props.data, _ => _.error);
  }

  isPermissionDenied(): boolean {
    return !idx(this.props.data, _ => _.me.permissions.can_edit_phone_call_preferences);
  }

  getPreferenceValue(): ?string {
    return idx(this.props.data, _ => _.me.preferences.receive_phone_calls);
  }

  isSmsSupported(): ?boolean {
    return idx(this.props.data, _ => _.me.sms_supported);
  }

  render() {
    if (this.isInitialDataMissing()) {
      return null;
    }

    if (this.hasInitialDataErrored()) {
      return null;
    }

    if (this.isPermissionDenied()) {
      return null;
    }

    if (!this.isSmsSupported()) {
      return null;
    }

    const value = this.getPreferenceValue();

    if (!value) {
      return null;
    }

    return (
      <PhoneCallPreferenceSection
        data-test="phone-call-preference-section"
        value={value}
        enable={this.enable}
        disable={this.disable}
        persisted={this.state.persisted}
        persistError={this.state.persistError}
        smsSupported={this.props.smsSupported}
      />
    );
  }
}

const withPhoneCallPreferenceMutation = graphql(gql`
  mutation updatePhoneCallPreference($value: String!) {
    updateUserPrefs(input: { receive_phone_calls: $value }) {
      user {
        uuid
        preferences {
          receive_phone_calls
        }
      }
    }
  }
`);

const withPermission = graphql(gql`
  query PhoneCallPreferencePermission {
    me {
      uuid
      sms_supported
      preferences {
        receive_phone_calls
      }
      permissions {
        can_edit_phone_call_preferences
      }
    }
  }
`);

export { PhoneCallPreferenceSectionContainer };
export default withPermission(withPhoneCallPreferenceMutation(PhoneCallPreferenceSectionContainer));
